type ImpersonationState = { enterpriseId: string; enterpriseName: string; expiresAt: number } | null
type Listener = () => void

const STORAGE_KEY = 'wispr_impersonation'
const TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

function loadFromStorage(): ImpersonationState {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored)
    if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
      sessionStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

let _state: ImpersonationState = loadFromStorage()
const _listeners = new Set<Listener>()
let _expiryTimer: ReturnType<typeof setTimeout> | null = null

function _scheduleExpiry() {
  if (_expiryTimer) clearTimeout(_expiryTimer)
  if (_state?.expiresAt) {
    const remaining = _state.expiresAt - Date.now()
    if (remaining <= 0) {
      setImpersonation(null)
      return
    }
    _expiryTimer = setTimeout(() => setImpersonation(null), remaining)
  }
}

// Start timer for any state loaded from storage
_scheduleExpiry()

export function getImpersonation(): ImpersonationState {
  return _state
}

export function setImpersonation(value: Omit<NonNullable<ImpersonationState>, 'expiresAt'> | null) {
  if (_expiryTimer) clearTimeout(_expiryTimer)
  _expiryTimer = null

  if (value) {
    _state = { ...value, expiresAt: Date.now() + TIMEOUT_MS }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(_state))
    _scheduleExpiry()
  } else {
    _state = null
    sessionStorage.removeItem(STORAGE_KEY)
  }
  _listeners.forEach((l) => l())
}

/** Subscribe to changes — returns unsubscribe function. For use with useSyncExternalStore. */
export function subscribeImpersonation(listener: Listener): () => void {
  _listeners.add(listener)
  return () => _listeners.delete(listener)
}
